import React, { useContext, useEffect, useState } from 'react';
import './ViewForm.css';
import axios from 'axios';
import { ConfigContext } from '../../Context/ConfigContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentLoader } from '../../Components/Shimmer';
import Select from "react-select";
import Swal from "sweetalert2";

const ViewForm = () => {
    const { inspection_id, project_id, project_request_id, contractor_id } = useParams();
    const { apiHeaderJson, apiURL, user_profile_id } = useContext(ConfigContext);

    const [questions, setQuestions] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [formHeader, setFormHeader] = useState({});
    const [responses, setResponses] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [comment, setComment] = useState('');
    const [consultantData, setConsultantData] = useState([]);
    const [consultantId, setConsultantId] = useState(0);

    const navigate = useNavigate();

    const handleInputChange = (questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleConsultantChange = (selectedOption) => {
        setConsultantId(selectedOption ? selectedOption.value : null);
    }

    const handleOptionChange = (questionId, optionId, isChecked, questionType) => {
        setResponses(prev => {
            const currentValue = prev[questionId] || (questionType === 'checkbox' ? [] : '');
            if (questionType === 'checkbox') {
                return {
                    ...prev,
                    [questionId]: isChecked
                        ? [...currentValue, optionId]
                        : currentValue.filter(id => id !== optionId)
                };
            } else {
                return {
                    ...prev,
                    [questionId]: optionId
                };
            }
        });
    };

    const getProjectConsultant = async () => {
        try {
            const response = await axios.get(`${apiURL}api/project-consultants/project/${project_id}`, {
                headers: apiHeaderJson
            });
            const { data } = response.data;
            setConsultantData(data.map(row => ({
                label: row.profile_name,
                value: row.consultant_id
            })));
        } catch (err) {
            console.error(err);
        }
    }

    const handleStatus = async () => {
        const headers = apiHeaderJson;
        const body = {
            project_request_id,
            consultant_id: user_profile_id,
            consultant_status: '1',
            consultant_remark: comment
        }

        try {
            const response = await axios.patch(`${apiURL}api/project-items/status`, body, { headers })
            const { success, message } = response.data;
            console.log('response =======>', response.data.success);


            Swal.fire({
                icon: success ? 'success' : "error",
                title: message,
                showConfirmButton: false,
                timer: 1500
            });

            if (success) {
                document.getElementById("close-create-modal").click();
            }
        } catch (error) {
            console.log('error', error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                showConfirmButton: false,
                timer: 1500
            });
        }

    };

    const validateForm = () => {
        const unansweredRequired = questions.filter(q =>
            q.required &&
            (!responses[q.question_id] ||
                (q.type === 'checkbox' && responses[q.question_id]?.length === 0))
        );
        if (unansweredRequired.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Please answer all required questions',
                text: `Question "${unansweredRequired[0].question}" is required`,
                showConfirmButton: true
            });
            return false;
        }
        return true;
    }

    const handleSubmitClick = async () => {
        if (!validateForm()) return;

        const formattedResponses = questions.map(q => {
            const responseValue = responses[q.question_id];
            if (q.type === 'checkbox' && Array.isArray(responseValue)) {
                return responseValue.map(optionId => ({
                    inspection_question_id: q.question_id,
                    inspection_option_id: optionId,
                    value: q.options.find(opt => opt.id === optionId)?.value || ''
                }));
            }
            if (q.type === 'multiple-choice' && responseValue) {
                return {
                    inspection_question_id: q.question_id,
                    inspection_option_id: responseValue,
                    value: q.options.find(opt => opt.id === responseValue)?.value || ''
                };
            }
            return {
                inspection_question_id: q.question_id,
                inspection_option_id: 0,
                value: responseValue || ''
            };
        }).flat();

        const payload = {
            inspection_id: parseInt(inspection_id),
            responses: formattedResponses,
            project_request_id,
            contractor_id,
            project_id,
            consultant_id: user_profile_id
        };

        try {
            const response = await axios.post(`${apiURL}api/inspections/SubmitInspection`, payload, {
                headers: apiHeaderJson
            });

            const { message, success } = response.data;

            if (success) {
                await Swal.fire({
                    icon: 'success',
                    title: message,
                    text: 'Now please take action.',
                    showConfirmButton: false,
                    timer: 1500
                });
                setShowModal(true);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: message,
                    showConfirmButton: false,
                    timer: 1500
                });
            }
        } catch (err) {
            console.error(err);
            await Swal.fire({
                icon: 'error',
                title: 'Error submitting form',
                text: err.message,
                showConfirmButton: true
            });
        }
    }

    const handleActionSubmit = async () => {
        const statusMap = {
            "Direct Approve": '1',
            "Reject": '3',
            "Revise": '2'
        };

        if (!selectedAction) {
            Swal.fire({
                icon: 'warning',
                title: 'Please select an action',
                timer: 1500,
                showConfirmButton: false
            });
            return;
        }

        const headers = apiHeaderJson;

        try {
            let response;

            if (selectedAction === "Clearance Required") {
                if (!consultantId) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Please select a consultant for Clearance Required',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    return;
                }

                // Call AssignForClearance API
                const formData = new FormData();
                formData.append("project_request_id", project_request_id);
                formData.append("project_id", project_id);
                formData.append("consultant_id", consultantId);
                formData.append("assigned_by", user_profile_id);
                formData.append("clearance_remark", comment);

                response = await axios.post(
                    `${apiURL}api/requests/AssignForClearance`,
                    formData,
                    { headers }
                );

            } else {
                // Call existing status API
                const body = {
                    project_request_id,
                    consultant_id: user_profile_id,
                    consultant_status: statusMap[selectedAction],
                    consultant_remark: comment
                };

                response = await axios.patch(
                    `${apiURL}api/project-items/status`,
                    body,
                    { headers }
                );
            }

            const { success, message } = response.data;

            await Swal.fire({
                icon: success ? 'success' : "error",
                title: message,
                showConfirmButton: false,
                timer: 1500
            });

            if (success) {
                setShowModal(false);
                navigate('/ViewAllRequest');
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Error taking action',
                showConfirmButton: false,
                timer: 1500
            });
        }
    };


    const handleModalCloseOrCancel = () => {
        setShowModal(false);
        navigate('/InspectionRequest');
    }

    const GetInfo = async () => {
        try {
            setLoadingList(true);
            const response = await axios.get(`${apiURL}api/inspections/GetInspectionsQuestions`, {
                params: { inspection_id }
            });
            const { data, success } = response.data;
            if (success) {
                setFormHeader(data.inspection);
                const sortedQuestions = [...(data?.questions || [])].sort(
                    (a, b) => a.question_id - b.question_id
                );
                setQuestions(sortedQuestions);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingList(false);
        }
    }

    useEffect(() => {
        getProjectConsultant();
        if (inspection_id) GetInfo();
    }, [inspection_id]);

    const renderPreviewQuestion = (question) => {
        switch (question.type) {
            case 'short-answer':
                return (
                    <input
                        type="text"
                        className="form-control google-input"
                        placeholder="Your answer"
                        value={responses[question.question_id] || ''}
                        onChange={(e) => handleInputChange(question.question_id, e.target.value)}
                    />
                );
            case 'paragraph':
                return (
                    <textarea
                        className="form-control google-input"
                        placeholder="Your answer"
                        rows={3}
                        value={responses[question.question_id] || ''}
                        onChange={(e) => handleInputChange(question.question_id, e.target.value)}
                    />
                );
            case 'multiple-choice':
                return (
                    <div className="options-container">
                        {question.options.map(opt => (
                            <div key={opt.id} className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name={`question-${question.question_id}`}
                                    checked={responses[question.question_id] === opt.id}
                                    onChange={() => handleOptionChange(question.question_id, opt.id, true, question.type)}
                                />
                                <label className="form-check-label">{opt.value || 'Option'}</label>
                            </div>
                        ))}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="options-container">
                        {question.options.map(opt => (
                            <div key={opt.id} className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={responses[question.question_id]?.includes(opt.id) || false}
                                    onChange={(e) => handleOptionChange(question.question_id, opt.id, e.target.checked, question.type)}
                                />
                                <label className="form-check-label">{opt.value || 'Option'}</label>
                            </div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    }

    return (
        <div className="main-content">
            <div className="page-content">
                {loadingList ? <ContentLoader /> : (
                    <div className="google-form-container">
                        <div className="form-preview">
                            <div className="preview-header">
                                <h1>{formHeader?.inspection_title || 'Untitled form'}</h1>
                                <p>{formHeader?.inspection_description}</p>
                            </div>

                            <div className="preview-questions">
                                {questions.map(q => (
                                    <div key={q.id} className="preview-question">
                                        <label>
                                            {q.question} {q.required && <span className="text-danger">*</span>}
                                        </label>
                                        {renderPreviewQuestion(q)}
                                    </div>
                                ))}
                            </div>

                            <div className="form-actions">
                                <button className="btn btn-info" onClick={() => navigate(-1)}>Go Back</button>
                                <button className="btn btn-primary ml-2" onClick={handleSubmitClick}>Submit</button>
                            </div>
                        </div>
                    </div>
                )}

                {showModal && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Take Action</h5>
                                    <button type="button" className="btn-close" onClick={handleModalCloseOrCancel}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
                                        {['Direct Approve', 'Revise', 'Reject', 'Clearance Required'].map(action => (
                                            <button
                                                key={action}
                                                className={`btn ${selectedAction === action ? 'btn-primary' : 'btn-outline-primary'} flex-fill`}
                                                onClick={() => setSelectedAction(action)}
                                                style={{ minWidth: '140px' }}
                                            >
                                                {action}
                                            </button>
                                        ))}
                                    </div>

                                    {selectedAction === 'Clearance Required' && (
                                        <div className="mb-3">
                                            <Select
                                                options={consultantData}
                                                onChange={handleConsultantChange}
                                                placeholder="Please select consultant"
                                                value={consultantId ? consultantData.find(opt => opt.value === consultantId) : null}
                                            />
                                        </div>
                                    )}

                                    <textarea
                                        className="form-control"
                                        placeholder="Comment / Remark"
                                        rows="4"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-warning"
                                        disabled={!selectedAction && !comment}
                                        onClick={() => {
                                            setSelectedAction('');
                                            setComment('');
                                            setConsultantId(0);
                                        }}
                                    >
                                        Clear
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleModalCloseOrCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleActionSubmit}
                                    >
                                        Submit Action
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ViewForm;
